import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface AdminPlaceRow {
  id: string;
  name: string;
  municipality: string | null;
  landskap: string | null;
  status: string;
  updated_at: string;
  editor_count: number;
}

export interface PlacesFilterState {
  search: string;
  status: string;
  landskap: string;
  municipality: string;
}

const DEFAULTS: PlacesFilterState = {
  search: "",
  status: "all",
  landskap: "all",
  municipality: "all",
};

/** Read filter state from URL search params */
function readParams(sp: URLSearchParams): PlacesFilterState {
  return {
    search: sp.get("q") ?? "",
    status: sp.get("status") ?? "all",
    landskap: sp.get("landscape") ?? "all",
    municipality: sp.get("city") ?? "all",
  };
}

/** Write filter state to URL search params (only non-default values) */
function writeParams(f: PlacesFilterState): URLSearchParams {
  const sp = new URLSearchParams();
  if (f.search.trim()) sp.set("q", f.search.trim());
  if (f.status !== "all") sp.set("status", f.status);
  if (f.landskap !== "all") sp.set("landscape", f.landskap);
  if (f.municipality !== "all") sp.set("city", f.municipality);
  return sp;
}

export function useAdminPlacesList() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive filter state from URL
  const filters = useMemo(() => readParams(searchParams), [searchParams]);

  // Debounced search for query
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync debounced search when URL changes (e.g. back navigation)
  useEffect(() => {
    setDebouncedSearch(filters.search);
  }, [filters.search]);

  const updateUrl = useCallback((next: PlacesFilterState) => {
    setSearchParams(writeParams(next), { replace: true });
  }, [setSearchParams]);

  const setSearch = useCallback((value: string) => {
    const next = { ...readParams(searchParams), search: value };
    updateUrl(next);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(value), 350);
  }, [searchParams, updateUrl]);

  const setFilter = useCallback(<K extends keyof PlacesFilterState>(key: K, value: PlacesFilterState[K]) => {
    const next = { ...readParams(searchParams), [key]: value };
    updateUrl(next);
  }, [searchParams, updateUrl]);

  const resetFilters = useCallback(() => {
    updateUrl(DEFAULTS);
    setDebouncedSearch("");
  }, [updateUrl]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.status !== "all") count++;
    if (filters.landskap !== "all") count++;
    if (filters.municipality !== "all") count++;
    if (debouncedSearch.trim()) count++;
    return count;
  }, [filters, debouncedSearch]);

  const queryKey = ["admin-places-list", debouncedSearch, filters.status, filters.landskap, filters.municipality];

  const { data: places, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      let q = supabase
        .from("places")
        .select("id, name, municipality, landskap, status, updated_at, place_editors(count)")
        .order("updated_at", { ascending: false })
        .limit(200);

      if (debouncedSearch.trim()) {
        q = q.ilike("name", `%${debouncedSearch.trim()}%`);
      }
      if (filters.status !== "all") {
        q = q.eq("status", filters.status);
      }
      if (filters.landskap !== "all") {
        q = q.eq("landskap", filters.landskap);
      }
      if (filters.municipality !== "all") {
        q = q.eq("municipality", filters.municipality);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []).map((row: any) => ({
        id: row.id,
        name: row.name,
        municipality: row.municipality,
        landskap: row.landskap,
        status: row.status,
        updated_at: row.updated_at,
        editor_count: row.place_editors?.[0]?.count ?? 0,
      })) as AdminPlaceRow[];
    },
  });

  const { data: filterOptions } = useQuery({
    queryKey: ["admin-places-filter-options"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("places")
        .select("landskap, municipality");
      if (error) throw error;
      const landskapSet = new Set<string>();
      const municipalitySet = new Set<string>();
      for (const row of data ?? []) {
        if (row.landskap) landskapSet.add(row.landskap);
        if (row.municipality) municipalitySet.add(row.municipality);
      }
      return { landskap: Array.from(landskapSet).sort(), municipality: Array.from(municipalitySet).sort() };
    },
    staleTime: 60_000,
  });

  return {
    places: places ?? [],
    isLoading,
    filters,
    setSearch,
    setFilter,
    resetFilters,
    activeFilterCount,
    filterOptions: filterOptions ?? { landskap: [], municipality: [] },
  };
}

export function useUpdatePlaceStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, newStatus }: { id: string; newStatus: string }) => {
      const { data, error } = await supabase
        .from("places")
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select("id, status")
        .single();
      if (error) throw error;
      return data;
    },
    onMutate: async ({ id, newStatus }) => {
      await queryClient.cancelQueries({ queryKey: ["admin-places-list"] });
      const previousQueries = queryClient.getQueriesData<AdminPlaceRow[]>({ queryKey: ["admin-places-list"] });
      queryClient.setQueriesData<AdminPlaceRow[]>({ queryKey: ["admin-places-list"] }, (old) =>
        old?.map((p) => (p.id === id ? { ...p, status: newStatus } : p))
      );
      return { previousQueries };
    },
    onError: (error, _vars, context) => {
      context?.previousQueries?.forEach(([key, data]) => { queryClient.setQueryData(key, data); });
      const msg = (error as any)?.code === "42501"
        ? "Du saknar behörighet att ändra status. Kontrollera RLS/GRANT."
        : "Kunde inte uppdatera status. Försök igen.";
      toast({ title: "Fel", description: msg, variant: "destructive" });
    },
    onSuccess: (_data, { newStatus }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-places-list"] });
      queryClient.invalidateQueries({ queryKey: ["admin-places"] });
      queryClient.invalidateQueries({ queryKey: ["published-places"] });
      toast({ title: newStatus === "published" ? "Platsen är nu publicerad." : "Platsen är nu avpublicerad." });
    },
  });
}
